import React, { useEffect, useRef, useState } from 'react';
import { Linkedin, Facebook, Instagram,Youtube } from 'lucide-react';
import { FaXTwitter } from 'react-icons/fa6';
import { FaWhatsapp } from 'react-icons/fa';
import GetInTouchForm from './GetinTouch';
import useTheme from '../ColorConfig/usetheme';

const ContactBanner = () => {
    const colors = useTheme();
    const geometryRef = useRef(null);
    const particlesRef = useRef(null);
    const [showGetInTouch, setShowGetInTouch] = useState(false);

    // Embedded Google Maps URL (same as in ContactDetails)
    const embeddedMapUrl = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3887.883874625863!2d80.2432405147935!3d13.045658551639726!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a5267cc6922b6f3%3A0xe12253cd93744052!2sIDLBAY!5e0!3m2!1sen!2sin!4v1694760773046!5m2!1sen!2sin";
    const mapsUrl = "https://maps.app.goo.gl/pdHsDX8t9KUrbgf49";

    const handleContactClick = (e) => {
        e.preventDefault();
        setShowGetInTouch(true);
        document.body.style.overflow = 'hidden';
    };

    const closeGetInTouch = () => {
        setShowGetInTouch(false);
        document.body.style.overflow = 'auto';
    };

    useEffect(() => {
        // Floating geometry animation - FASTER
        const geometryElement = geometryRef.current;
        if (geometryElement) {
            geometryElement.style.animation = 'floatGeometry 8s linear infinite';
        }

        // Particle system animation - FASTER
        const particlesElement = particlesRef.current;
        if (particlesElement) {
            particlesElement.style.animation = 'particleFloat 6s linear infinite';
        }
    }, []);

    const handlePhoneClick = () => {
        window.location.href = 'tel:+919840841840';
    };

    const handleEmailClick = () => {
        window.location.href = 'mailto:contact@idlbay.com';
    };

    const handleAddressClick = () => {
        window.open(mapsUrl, '_blank');
    };

    const whatsappMessage = "Hello! I'm interested in learning more about your services.";
    
    const handleWhatsAppClick = () => {
        const url = `https://wa.me/919840841840?text=${encodeURIComponent(whatsappMessage)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    return (
        <>
            <style jsx>{`
                @keyframes slideInLeft {
                    from {
                        opacity: 0;
                        transform: translateX(-100px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                @keyframes slideInRight {
                    from {
                        opacity: 0;
                        transform: translateX(100px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }

                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(50px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                /* FASTER ANIMATIONS */
                @keyframes floatGeometry {
                    0% {
                        transform: translateY(0px) rotate(0deg) scale(1);
                    }
                    25% {
                        transform: translateY(-15px) rotate(90deg) scale(1.05);
                    }
                    50% {
                        transform: translateY(0px) rotate(180deg) scale(1);
                    }
                    75% {
                        transform: translateY(-10px) rotate(270deg) scale(1.05);
                    }
                    100% {
                        transform: translateY(0px) rotate(360deg) scale(1);
                    }
                }

                @keyframes particleFloat {
                    0% {
                        transform: translateX(0) translateY(0) rotate(0deg);
                    }
                    33% {
                        transform: translateX(20px) translateY(-15px) rotate(120deg);
                    }
                    66% {
                        transform: translateX(-15px) translateY(-8px) rotate(240deg);
                    }
                    100% {
                        transform: translateX(0) translateY(0) rotate(360deg);
                    }
                }

                /* NEW UNIQUE BACKGROUND ANIMATIONS */
                @keyframes cyberGrid {
                    0% {
                        background-position: 0 0;
                    }
                    100% {
                        background-position: 50px 50px;
                    }
                }

                @keyframes binaryRain {
                    0% {
                        background-position: 0 0;
                    }
                    100% {
                        background-position: 0 100%;
                    }
                }

                @keyframes circuitFlow {
                    0% {
                        stroke-dashoffset: 1000;
                    }
                    100% {
                        stroke-dashoffset: 0;
                    }
                }

                @keyframes hologramFlicker {
                    0%, 100% {
                        opacity: 0.8;
                        transform: skewX(0deg);
                    }
                    25% {
                        opacity: 0.9;
                        transform: skewX(0.5deg);
                    }
                    50% {
                        opacity: 0.7;
                        transform: skewX(-0.5deg);
                    }
                    75% {
                        opacity: 0.85;
                        transform: skewX(0.3deg);
                    }
                }

                @keyframes dataStream {
                    0% {
                        transform: translateY(-100%) rotate(0deg);
                    }
                    100% {
                        transform: translateY(100%) rotate(360deg);
                    }
                }

                @keyframes neonPulse {
                    0%, 100% {
                        opacity: 0.6;
                        filter: brightness(1);
                    }
                    50% {
                        opacity: 1;
                        filter: brightness(1.5);
                    }
                }

                @keyframes matrixCode {
                    0% {
                        background-position: 0 0;
                    }
                    100% {
                        background-position: 0 100vh;
                    }
                }

                .animate-slide-left {
                    animation: slideInLeft 0.6s ease-out; /* FASTER */
                }

                .animate-slide-right {
                    animation: slideInRight 0.6s ease-out; /* FASTER */
                }

                .animate-fade-up {
                    animation: fadeInUp 0.6s ease-out 0.2s both; /* FASTER */
                }

                .animate-fade-up-delay {
                    animation: fadeInUp 0.6s ease-out 0.4s both; /* FASTER */
                }

                .animate-pulse {
                    animation: pulse 1.5s ease-in-out infinite; /* FASTER */
                }

                .btn-hover-effect {
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); /* FASTER */
                    position: relative;
                    overflow: hidden;
                }

                .btn-hover-effect::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
                    transition: left 0.3s; /* FASTER */
                }

                .btn-hover-effect:hover::before {
                    left: 100%;
                }

                .contact-info-item {
                    animation: fadeInUp 0.6s ease-out both; /* FASTER */
                    cursor: pointer;
                    transition: transform 0.15s ease, background-color 0.15s ease; /* FASTER */
                    padding: 10px;
                    border-radius: 8px;
                }

                .contact-info-item:hover {
                    transform: translateY(-3px);
                }

                .contact-info-item:nth-child(1) {
                    animation-delay: 0.1s; /* FASTER */
                }

                .contact-info-item:nth-child(2) {
                    animation-delay: 0.2s; /* FASTER */
                }

                .contact-info-item:nth-child(3) {
                    animation-delay: 0.3s; /* FASTER */
                }

                .contact-info-item:nth-child(4) {
                    animation-delay: 0.4s; /* FASTER */
                }

                .clickable-text {
                    cursor: pointer;
                    transition: color 0.15s ease; /* FASTER */
                }

                .clickable-text:hover {
                    color: var(--color-bodyBlack) !important;
                }

                .social-links {
                    display: flex;
                    gap: 15px;
                    flex-wrap: wrap;
                    margin-top: 10px;
                }

                .social-link {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 40px;
                    height: 40px;
                    background-color: rgba(255, 255, 255, 0.2);
                    border-radius: 50%;
                    color: white;
                    text-decoration: none;
                    transition: all 0.2s ease; /* FASTER */
                    box-shadow: 0 4px 15px rgba(0,0,0,0.2);
                }

                .social-link:hover {
                    background-color: rgba(255, 255, 255, 0.3);
                    transform: translateY(-3px);
                    color: white;
                    box-shadow: 0 6px 20px rgba(0,0,0,0.3);
                }

                /* NEW BACKGROUND ANIMATION STYLES */
                .cyber-grid-bg {
                    background-image: 
                        linear-gradient(rgba(var(--color-primary-rgb), 0.1) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(var(--color-primary-rgb), 0.1) 1px, transparent 1px);
                    background-size: 50px 50px;
                    animation: cyberGrid 3s linear infinite; /* FASTER */
                }

                .binary-rain-bg {
                    background-image: 
                        linear-gradient(90deg, 
                            transparent 0%, 
                            rgba(var(--color-primary-rgb), 0.3) 50%, 
                            transparent 100%);
                    background-size: 100% 4px;
                    animation: binaryRain 2s linear infinite; /* FASTER */
                }

                .hologram-effect {
                    animation: hologramFlicker 4s ease-in-out infinite; /* FASTER */
                    background: 
                        linear-gradient(45deg, 
                            transparent 0%, 
                            rgba(var(--color-primary-rgb), 0.1) 50%, 
                            transparent 100%);
                }

                .data-stream-bg {
                    position: relative;
                    overflow: hidden;
                }

                .data-stream-bg::before {
                    content: '';
                    position: absolute;
                    top: -100%;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(
                        0deg,
                        transparent 0%,
                        rgba(var(--color-primary-rgb), 0.2) 50%,
                        transparent 100%
                    );
                    animation: dataStream 3s linear infinite; /* FASTER */
                }

                .matrix-code-bg {
                    background-image: 
                        radial-gradient(circle at 25% 25%, rgba(var(--color-primary-rgb), 0.1) 0%, transparent 50%),
                        radial-gradient(circle at 75% 75%, rgba(var(--color-primary-rgb), 0.1) 0%, transparent 50%);
                    background-size: 200% 200%;
                    animation: matrixCode 10s ease-in-out infinite; /* FASTER */
                }

                .neon-circuit-bg {
                    background-image: 
                        url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none'/%3E%3Cpath d='M20 20h60v60H20z' stroke='rgba(var(--color-primary-rgb),0.3)' stroke-width='1' fill='none' stroke-dasharray='5 5'/%3E%3Cpath d='M30 30h40v40H30z' stroke='rgba(var(--color-primary-rgb),0.2)' stroke-width='1' fill='none' stroke-dasharray='3 3'/%3E%3C/svg%3E");
                    background-size: 100px 100px;
                    animation: neonPulse 2s ease-in-out infinite; /* FASTER */
                }

                /* Mobile Responsive Styles */
                @media (max-width: 768px) {
                    .contact-container {
                        flex-direction: column !important;
                        height: auto !important;
                        min-height: auto !important;
                    }

                    .left-section {
                        min-height: 35vh !important;
                        padding: 20px 15px !important;
                        display: flex !important;
                        align-items: center !important;
                    }

                    .right-section {
                        min-width: 100% !important;
                        min-height: 50vh !important;
                        padding: 20px !important;
                    }

                    .main-title {
                        font-size: 2.5rem !important;
                        margin-left: 0 !important;
                    }

                    .main-content {
                        margin-left: 0 !important;
                        margin-top: -30px !important;
                        margin-bottom: 0 !important;
                        padding-top: 10px !important;
                        padding-bottom: 10px !important;
                    }

                    .title-container {
                        margin-left: 0 !important;
                        margin-bottom: 1rem !important;
                    }

                    .social-links {
                        justify-content: flex-start;
                    }

                    .modal-content-custom {
                        max-width: 95%;
                        margin: 20px;
                    }

                    .contact-des {
                        font-size: 0.9rem !important;
                        margin-bottom: 1.5rem !important;
                    }

                    .contact-send-btn {
                        margin-left: 0 !important;
                        width: 100%;
                        max-width: 200px;
                    }

                    .container-fluid {
                        padding-top: 5px !important;
                        padding-bottom: 5px !important;
                    }
                }

                @media (max-width: 480px) {
                    .left-section {
                        min-height: 30vh !important;
                        padding: 15px 10px !important;
                    }

                    .main-title {
                        font-size: 1.8rem !important;
                        margin-top: 0 !important;
                        line-height: 1.2 !important;
                    }
                    
                    .contact-line {
                        width: 3px !important;
                        height: 35px !important;
                        margin-left: 0 !important;
                    }
                    
                    .contact-des {
                        max-width: 100% !important;
                        font-size: 0.85rem !important;
                        line-height: 1.5 !important;
                        margin-left: 0 !important;
                        margin-bottom: 1rem !important;
                        padding-right: 10px;
                    }
                    
                    .contact-send-btn {
                        border: none !important;
                        border-radius: 5px !important;
                        margin-left: 0 !important;
                        position: relative !important;
                        width: 100%;
                        max-width: 180px;
                        font-size: 0.9rem !important;
                        padding: 12px 20px !important;
                    }

                    .title-container {
                        margin-bottom: 0.8rem !important;
                    }

                    .main-content {
                        padding-left: 0 !important;
                        margin-top: -20px !important;
                        margin-bottom: 0 !important;
                        padding-top: 0 !important;
                        padding-bottom: 0 !important;
                    }

                    .container-fluid {
                        padding-left: 15px !important;
                        padding-right: 15px !important;
                        padding-top: 0 !important;
                        padding-bottom: 0 !important;
                    }

                    .ms-5 {
                        margin-left: 0 !important;
                    }

                    .contact-info-item {
                        padding: 8px;
                    }

                    .social-links {
                        gap: 10px;
                    }

                    .social-link {
                        width: 35px;
                        height: 35px;
                    }

                    .right-section h2 {
                        font-size: 1.4rem !important;
                    }

                    .right-section .contact-info-item h4 {
                        font-size: 0.9rem !important;
                    }

                    .right-section .contact-info-item p {
                        font-size: 0.75rem !important;
                    }
                }

                @media (max-width: 375px) {
                    .left-section {
                        min-height: 25vh !important;
                        padding: 10px 8px !important;
                    }

                    .main-title {
                        font-size: 1.6rem !important;
                        margin-top: 0 !important;
                    }
                    
                    .contact-des {
                        font-size: 1.0rem !important;
                        margin-bottom: 0.8rem !important;
                    }
                    
                    .contact-send-btn {
                        font-size: 0.85rem !important;
                        padding: 10px 16px !important;
                        max-width: 160px;
                        margin-bottom: 20px;
                    }

                    .container-fluid {
                        padding-left: 10px !important;
                        padding-right: 10px !important;
                        padding-top: 0 !important;
                        padding-bottom: 0 !important;
                    }

                    .title-container {
                        margin-bottom: 0.5rem !important;
                    }

                    .main-content {
                        padding-top: 0 !important;
                        padding-bottom: 0 !important;
                        margin-top: -15px !important;
                        margin-bottom: 0 !important;
                    }
                }
            `}</style>

            <div className="d-flex contact-container" style={{ height: '80vh', minHeight: '530px', backgroundColor: colors.bodyBlack }}>
                {/* Left Section - Main Content */}
                <div 
                    className="flex-fill d-flex align-items-center position-relative left-section"
                    style={{
                        background: `linear-gradient(135deg, ${colors.bodyBlack}, ${colors.bodyBlack})`,
                        overflow: 'hidden',
                        backgroundAttachment: 'fixed'
                    }}
                >
                    {/* NEW: Multiple Background Animations */}
                    <div 
                        className="position-absolute w-100 h-100 cyber-grid-bg"
                    />
                    
                    <div 
                        className="position-absolute w-100 h-100 binary-rain-bg"
                    />
                    
                    <div 
                        className="position-absolute w-100 h-100 hologram-effect"
                    />
                    
                    <div 
                        className="position-absolute w-100 h-100 data-stream-bg"
                    />
                    
                    <div 
                        className="position-absolute w-100 h-100 matrix-code-bg"
                    />

                    {/* Floating Code Elements - FASTER */}
                    <div 
                        ref={particlesRef}
                        className="position-absolute w-100 h-100"
                        style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 800'%3E%3Cg opacity='0.1' fill='%23var(--color-primary)'%3E%3Ctext x='100' y='100' font-family='monospace' font-size='16'%3E&lt;/&gt;%3C/text%3E%3Ctext x='300' y='200' font-family='monospace' font-size='14'%3E{ }%3C/text%3E%3Ctext x='500' y='150' font-family='monospace' font-size='18'%3E[0101]%3C/text%3E%3Ctext x='700' y='300' font-family='monospace' font-size='16'%3Efunction()%3C/text%3E%3Ctext x='200' y='400' font-family='monospace' font-size='14'%3E====%3C/text%3E%3Ctext x='800' y='450' font-family='monospace' font-size='16'%3E&amp;&amp;%3C/text%3E%3Ctext x='400' y='500' font-family='monospace' font-size='18'%3E#code%3C/text%3E%3Ctext x='600' y='600' font-family='monospace' font-size='14'%3Eif(true)%3C/text%3E%3C/g%3E%3C/svg%3E")`,
                            backgroundSize: 'cover'
                        }}
                    />
                    
                    {/* Animated Geometric Shapes - FASTER */}
                    <div 
                        ref={geometryRef}
                        className="position-absolute w-100 h-100 neon-circuit-bg"
                    />
                    
                    <div className="container-fluid px-5">
                        <div className="position-relative main-content" style={{ zIndex: 2, marginTop: '-30px' }}>
                            <div className="d-flex align-items-start mb-4 animate-slide-left title-container" style={{ marginLeft: "40px"}}>
                                <div 
                                    className="me-3 contact-line"
                                    style={{
                                        width: '4px',
                                        height: '60px',
                                        backgroundColor: `var(--color-primary)`,
                                        boxShadow: `0 0 20px var(--color-primary)`,
                                        animation: 'neonPulse 1.5s ease-in-out infinite' // FASTER
                                    }}
                                />
                                <h1 
                                    className="display-3 fw-bold mb-0 main-title"
                                    style={{ 
                                        color: colors.white,
                                        textShadow: `0 0 30px var(--color-primary)40`
                                    }}
                                >
                                    Contact Us
                                </h1>
                            </div>
                            
                            <div className="ms-5">
                                <p 
                                    className="fs-5 mb-4 animate-fade-up contact-des"
                                    style={{ 
                                        color: colors.white, 
                                        lineHeight: '1.6',
                                        maxWidth: '600px'
                                    }}
                                >
                                    Get in touch with our expert team for all your software development and technology needs. 
                                    We're here to help bring your digital vision to life with innovative solutions.
                                </p>
                                
                                <button 
                                    className="btn btn-lg fw-bold px-3 py-2 btn-hover-effect animate-fade-up-delay contact-send-btn"
                                    style={{ 
                                        backgroundColor: `var(--color-primary)`, 
                                        color: colors.white,
                                        border: 'none',
                                        letterSpacing: '0.5px',
                                        borderRadius: '5px',
                                        boxShadow: `0 4px 15px var(--color-primary)50`,
                                        position: 'relative',
                                    }}
                                    onClick={handleContactClick}
                                >
                                    Send a Message
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Section - Contact Info */}
                <div 
                    className="d-flex flex-column animate-slide-right right-section"
                    style={{ 
                        background: `linear-gradient(135deg, ${colors.primaryLight} 0%, var(--color-primary) 25%, var(--color-primary) 50%, var(--color-primary)75%, var(--color-primary)100%)`,
                        minWidth: '500px',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    {/* NEW: Animated Tech Pattern Overlay */}
                    <div 
                        className="position-absolute w-100 h-100 neon-circuit-bg"
                    />
                    
                    <div 
                        className="position-absolute w-100 h-100 hologram-effect"
                        style={{ opacity: 0.4 }}
                    />

                    <div className="p-4 flex-fill d-flex flex-column justify-content-center position-relative" style={{ zIndex: 2 }}>
                        <h2 
                            className="fw-bold mb-4"
                            style={{ 
                                color: colors.white,
                                fontSize: '1.8rem',
                                letterSpacing: '-1px',
                                textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                                animation: 'neonPulse 2s ease-in-out infinite' // FASTER
                            }}
                        >
                            Contact Info
                        </h2>
                        
                        {/* Office Address */}
                        <div className="d-flex align-items-start mb-3 contact-info-item" 
                             onClick={handleAddressClick}>
                            <div 
                                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 me-3 animate-pulse"
                                style={{ 
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    width: '45px',
                                    height: '45px',
                                    boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
                                }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="white"/>
                                </svg>
                            </div>
                            <div>
                                <h4 className="fw-bold mb-1" style={{ color: colors.white, fontSize: '1rem' }}>
                                    Our Office
                                </h4>
                                <p className="mb-0 clickable-text" style={{ color: colors.white, fontSize: '0.8rem', lineHeight: '1.4' }}>
                                    Office No.1, First Floor, Door No.1,<br />
                                    B Narasimhan 3rd Street, T-Nagar,<br />
                                    Chennai, Tamil Nadu 600017
                                </p>
                            </div>
                        </div>

                        {/* Email */}
                        <div className="d-flex align-items-start mb-3 contact-info-item" 
                             onClick={handleEmailClick}>
                            <div 
                                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 me-3 animate-pulse"
                                style={{ 
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    width: '45px',
                                    height: '45px',
                                    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                                    animationDelay: '0.5s' // FASTER
                                }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="white" strokeWidth="2" fill="none"/>
                                    <polyline points="22,6 12,13 2,6" stroke="white" strokeWidth="2" fill="none"/>
                                </svg>
                            </div>
                            <div>
                                <h4 className="fw-bold mb-1" style={{ color: colors.white, fontSize: '1rem' }}>
                                    Email
                                </h4>
                                <p className="mb-0 clickable-text" style={{ color: colors.white, fontSize: '0.8rem', lineHeight: '1.4' }}>
                                    contact@idlbay.com
                                </p>
                            </div>
                        </div>

                        {/* Phone */}
                        <div className="d-flex align-items-start mb-3 contact-info-item" 
                             onClick={handlePhoneClick}>
                            <div 
                                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 me-3 animate-pulse"
                                style={{ 
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    width: '45px',
                                    height: '45px',
                                    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                                    animationDelay: '1s' // FASTER
                                }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M21 15.46l-5.27-.61-2.52 2.52c-2.83-1.44-5.15-3.75-6.59-6.59l2.53-2.53L8.54 3H3.03C2.45 13.18 10.82 21.55 21 20.97v-5.51z" fill="white"/>
                                </svg>
                            </div>
                            <div>
                                <h4 className="fw-bold mb-1" style={{ color: colors.white, fontSize: '1rem' }}>
                                    Phone
                                </h4>
                                <p className="mb-0 clickable-text" style={{ color: colors.white, fontSize: '0.8rem', lineHeight: '1.4' }}>
                                    +91 9840 841 840
                                </p>
                            </div>
                        </div>

                        {/* Social Links */}
                        <div className="contact-info-item">
                            <h4 className="fw-bold mb-2" style={{ color: colors.white, fontSize: '1rem' }}>
                                Follow Us
                            </h4>
                            <div className="social-links">
                                <a href="https://www.linkedin.com/company/idlbay" className="social-link"  target="_blank"
                                   rel="noopener noreferrer" aria-label="LinkedIn">
                                    <Linkedin size={18} />
                                </a>
                                <a href="https://www.instagram.com/idlbay.official/" 
                                   className="social-link" 
                                   target="_blank"
                                   rel="noopener noreferrer"
                                   aria-label="Instagram">
                                    <Instagram size={18} />
                                </a>
                                {/* <a href="#"
                                   className="social-link"
                                   onClick={(e) => {
                                       e.preventDefault();
                                       handleWhatsAppClick();
                                   }}
                                   aria-label="Contact us on WhatsApp">
                                    <FaWhatsapp size={18} />
                                </a> */}
                                  <a href="https://x.com/idlbayPvtLtd" 
                                   className="social-link" 
                                   target="_blank"
                                   rel="noopener noreferrer"
                                   aria-label="Instagram">
                                    <FaXTwitter size={18} />
                                </a>
                                  <a href="https://www.facebook.com/profile.php?id=61582746578911" 
                                   className="social-link" 
                                   target="_blank"
                                   rel="noopener noreferrer"
                                   aria-label="Instagram">
                                    <Facebook size={18} />
                                </a>
                                  <a href="https://www.youtube.com/channel/UCDSb9__-mSLqeyuZYelakcw" 
                                   className="social-link" 
                                   target="_blank"
                                   rel="noopener noreferrer"
                                   aria-label="Instagram">
                                    <Youtube size={18} />
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Modal */}
                {showGetInTouch && (
                    <div className="modal-overlay" onClick={closeGetInTouch}>
                        <div className="modal-content-custom" onClick={(e) => e.stopPropagation()}>
                            <button className="close-button" onClick={closeGetInTouch}>
                                &times;
                            </button>
                            <GetInTouchForm isModal={true} />
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default ContactBanner;